import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Users, Plus, Edit2, UserX, UserCheck, Key, Search, X, Shield, ChevronDown, ChevronUp, Trash2, Pause, Archive, Lock, Unlock, MoreVertical, Eye, EyeOff } from 'lucide-react';
import { NEU } from '../neuStyles';
import PrintButton from '../components/PrintButton';
import { VALID_ROLES, roleLabel } from '../utils/roleLabels';
import { refusalMessage } from '../lib/refusalMessage';

// VALID_ROLES / role labels live in ../utils/roleLabels (single source of
// truth, kept in sync with database/rbac.js ROLE_PERMISSIONS and the
// users_role_check constraint). ROLE_COLORS below is display-only.
const ROLE_COLORS = {
  admin: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
  board_of_directors: { bg: '#fdf4ff', color: '#6b21a8', border: '#e9d5ff' },
  physician: { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe' },
  nurse: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  researcher: { bg: '#faf5ff', color: '#6b21a8', border: '#e9d5ff' },
  manager: { bg: '#fff7ed', color: '#9a3412', border: '#fed7aa' },
  sales: { bg: '#ecfdf5', color: '#065f46', border: '#a7f3d0' },
  business: { bg: '#fefce8', color: '#854d0e', border: '#fef08a' },
  concierge: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  staff: { bg: '#eff6ff', color: '#1e3a8a', border: '#bfdbfe' },
  sysadmin: { bg: '#fff1f2', color: '#9f1239', border: '#fecdd3' },
  secretary: { bg: '#f0f9ff', color: '#075985', border: '#bae6fd' },
  programmer: { bg: '#f5f3ff', color: '#5b21b6', border: '#ddd6fe' },
  network_engineer: { bg: '#ecfeff', color: '#155e75', border: '#a5f3fc' },
  other: { bg: '#f9fafb', color: '#374151', border: '#d1d5db' },
  readonly: { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
};

const STATUS_COLORS = {
  active: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0', label: 'Active' },
  suspended: { bg: '#fefce8', color: '#854d0e', border: '#fef08a', label: 'Suspended' },
  archived: { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db', label: 'Archived' },
  deleted: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', label: 'Deleted' },
};

/* ── Users-table row treatment (#266) ─────────────────────────────────────────
 * ⚠ A NOTE ON "NEUMORPHIC". src/neuStyles.js describes itself as "Modern Flat
 * Style" — it was migrated away from neumorphism, and NEU.td/NEU.th explicitly
 * set `boxShadow: none`. So there is no neumorphic row treatment in there to
 * reuse; the house style for a surface is now `bgCard` + `border` + a small
 * drop shadow (NEU.card / NEU.cardSm). This applies THAT, which is what "carry
 * the house treatment" means today.
 *
 * ⚠ AND THE TENSION IS REAL. A soft shadow on a near-background surface is
 * low-contrast by construction, which is plausibly why these rows scan badly in
 * the first place. Shadow alone did NOT make 28 rows trackable when measured in
 * a browser, so this deliberately combines three cues rather than relying on the
 * shadow: an explicit surface, alternating elevation between adjacent rows, and
 * a real divider. See the PR body for the screenshot and the judgement.
 */
const ROW_BG_A = '#FFFFFF';
const ROW_BG_B = '#FAFBFD';   // alternating elevation — adjacent rows differ
const HOVER_BG = '#EEF4F6';   // teal-tinted, distinct from both bands
const ROW_H = 52;             // fixed row height — no more 2- and 3-line rows

const rowSurface = (idx, highlighted) => ({
  background: highlighted ? '#F0FDF4' : (idx % 2 === 0 ? ROW_BG_A : ROW_BG_B),
  boxShadow: highlighted ? 'inset 3px 0 0 #16a34a' : NEU.shadow.xs,
  borderBottom: `1px solid ${NEU.border}`,
  transition: 'background 0.15s, box-shadow 0.15s',
});

/** Fixed-height cell: one line per field, ellipsis, full value on hover. */
const CELL = { padding: '0 10px', height: ROW_H, verticalAlign: 'middle' };
// ⚠ RAISING maxWidth IS FREE; LOWERING IT IS NOT. Two names that differ only in a
// trailing word render IDENTICALLY once the ellipsis lands — `Ariel De la Rosa`
// and `Ariel De la Rosa Torres` are exactly that pair — and the only recovery is
// a native `title` tooltip on a ~500ms hover delay. Measured 2026-08-11 against
// the 28-row set: the widest display_name is `Jose Antonio Villalobos` at 177px
// at the shipping font/weight, so nothing truncates today and there is 83px of
// headroom. Anyone tightening this below ~180px is choosing that failure mode.
const TRUNCATE = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 };

/* Inline dropdown menu for row actions — renders via portal to avoid overflow:hidden clipping */
function ActionMenu({ items }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 2, left: rect.right - 160 });
    }
    setOpen(o => !o);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button ref={btnRef} className="btn-ghost btn-sm" title="More actions"
        onClick={handleToggle}
        style={{ display: 'flex', alignItems: 'center', padding: '4px 4px', fontSize: 11, color: '#6b7280' }}>
        <MoreVertical size={14} />
      </button>
      {open && ReactDOM.createPortal(
        <div ref={menuRef} style={{
          position: 'fixed', top: pos.top, left: Math.max(8, pos.left), zIndex: 9999,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 160, padding: '4px 0',
        }}>
          {items.map((item, i) => (
            <button key={i}
              onClick={() => { setOpen(false); item.onClick(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 14px', fontSize: 12, color: item.color || '#374151',
                background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              {item.icon} {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default function UserManagementPage({ user, onNavigate }) {
  const [users, setUsers] = useState([]);
  // #263: split so a refetch never replaces the table (see fetchUsers).
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [changeRoleUser, setChangeRoleUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [reactivateUser, setReactivateUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortField, setSortField] = useState('display_name');
  const [sortDir, setSortDir] = useState('asc');
  const [importingStaff, setImportingStaff] = useState(false);
  const [staffImportResult, setStaffImportResult] = useState(null);

  // RBAC UI-keying: gate on the permission each control's backend enforces, not
  // a role name, so UI visibility == backend allow (board no longer holds these
  // perms → its phantom controls disappear). Per-user override management +
  // password reset/lock are users:update; importing staff is users:create.
  const canManagePermissions = onNavigate && !!user?.permissions?.includes('users:update');
  const isAdmin = !!user?.permissions?.includes('users:update');
  const canImportStaff = !!user?.permissions?.includes('users:create');

  // Admin quick-action panel state
  const [quickSearch, setQuickSearch] = useState('');
  const [quickSelectedUser, setQuickSelectedUser] = useState(null);
  const [quickDropdownOpen, setQuickDropdownOpen] = useState(false);
  const quickRef = React.useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (quickRef.current && !quickRef.current.contains(e.target)) setQuickDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Alphabetically sorted, searchable user list for quick panel
  const quickFilteredUsers = users
    .filter(u => {
      const status = u.status || (u.is_active ? 'active' : 'suspended');
      return status !== 'deleted';
    })
    .filter(u => {
      if (!quickSearch.trim()) return true;
      const q = quickSearch.toLowerCase();
      return (u.display_name || '').toLowerCase().includes(q)
        || (u.username || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q);
    })
    .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));

  // #263: the confirmation is anchored to the ROW that changed, so it names the
  // record and survives being read late.
  //
  // ⚠ THE SCROLL IS CLAMPED, NOT SCROLLED, AND THAT IS WHY THERE IS NO RESTORE
  // CODE HERE. Measured (browser, 28 rows): after a change the container landed
  // at 0 on a 900px viewport and 190 on a 700px one — different residuals for
  // different collapsed layouts, which is the signature of the browser clamping
  // scrollTop against a shrunken scrollHeight, not of anything scrolling. The
  // shrink came from THIS component replacing the whole table with the
  // "Loading users…" placeholder on every refetch.
  //
  // So the fix is to not unmount: `initialLoading` gates the placeholder on
  // first mount only, and a post-mutation refetch sets `refreshing`, which
  // changes opacity and nothing about layout height. The refetch is still
  // authoritative — the server remains the source of truth, the rows just stay
  // on screen while it is in flight.
  //
  // An earlier revision restored scrollTop via document.querySelector('main.app-main').
  // It was deleted deliberately: a selector pointing at another component's
  // class name fails SILENTLY when that shell is renamed (`if (s)` skips, every
  // test stays green, an operator loses their place again with no signal). That
  // is #263's own failure mode — a thing that works until it quietly doesn't —
  // and the same move as #264: stop reaching for a hand-maintained handle and
  // remove the reason to need one.
  const [justChangedId, setJustChangedId] = useState(null);

  const noteRowChanged = useCallback((id) => {
    setJustChangedId(id);
    setTimeout(() => setJustChangedId(cur => (cur === id ? null : cur)), 6000);
  }, []);

  const fetchUsers = useCallback(async () => {
    setRefreshing(true);
    try {
      const result = await window.zignagenixAPI.getUsers();
      // Handle error responses from API (permission denied, SQL errors, etc.)
      if (result && result.error) {
        console.error('API error fetching users:', result.code, result.message);
        setError(result.message || 'Failed to load users');
        setUsers([]);
        return;
      }
      const items = Array.isArray(result) ? result : (result?.data || []);
      setUsers(items);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setRefreshing(false);
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleImportStaff = async () => {
    if (!window.confirm('Create user accounts from the ZignaGenix Staff & Team?\n\nThis will read the CRM staff list and create login accounts for any team members who don\'t already have one. Duplicates will be skipped.')) return;
    setImportingStaff(true);
    setStaffImportResult(null);
    try {
      const result = await window.zignagenixAPI.createUsersFromStaff();
      if (result?.error) { setError(result.message); setImportingStaff(false); return; }
      setStaffImportResult(result);
      if (result.created > 0) {
        setSuccess(`Created ${result.created} user account(s) from staff (${result.skipped} already existed)`);
        fetchUsers();
      } else {
        setSuccess(`All ${result.skipped} staff members already have user accounts — no duplicates created.`);
      }
    } catch (err) {
      setError('Failed to import staff users');
    } finally {
      setImportingStaff(false);
    }
  };

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 4000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 6000); return () => clearTimeout(t); }
  }, [error]);

  const filteredUsers = users
    .filter(u => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      const uStatus = u.status || (u.is_active ? 'active' : 'suspended');
      if (statusFilter !== 'all' && uStatus !== statusFilter) return false;
      if (searchText.trim()) {
        const s = searchText.toLowerCase();
        return (u.display_name || '').toLowerCase().includes(s)
          || (u.username || '').toLowerCase().includes(s)
          || (u.email || '').toLowerCase().includes(s);
      }
      return true;
    })
    .sort((a, b) => {
      let va = a[sortField] || '';
      let vb = b[sortField] || '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleSuspend = async (userId, username) => {
    const reason = window.prompt(`Suspend user "${username}"?\nOptionally enter a reason:`);
    if (reason === null) return; // cancelled
    try {
      const result = await window.zignagenixAPI.suspendUser(userId, reason || '');
      if (result?.error) { setError(result.message); return; }
      setSuccess(`User "${username}" has been suspended`);
      fetchUsers();
    } catch (err) { setError('Failed to suspend user'); }
  };

  const handleArchive = async (userId, username) => {
    if (!window.confirm(`Archive user "${username}"? Archived users cannot log in but their records are preserved.`)) return;
    try {
      const result = await window.zignagenixAPI.archiveUser(userId, '');
      if (result?.error) { setError(result.message); return; }
      setSuccess(`User "${username}" has been archived`);
      fetchUsers();
    } catch (err) { setError('Failed to archive user'); }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Delete user "${username}"?\n\nThis will soft-delete the account (HIPAA requires retaining records). The user will no longer appear in active lists.`)) return;
    try {
      const result = await window.zignagenixAPI.deleteUser(userId);
      if (result?.error) { setError(result.message); return; }
      setSuccess(`User "${username}" has been deleted`);
      fetchUsers();
    } catch (err) { setError('Failed to delete user'); }
  };

  // #266: four accounts were reactivated when three were intended, because
  // Reactivate fired on a single click with no confirmation. This now goes
  // through the same two-step confirm the Change Role modal already uses,
  // echoing name + username + what will happen.
  const handleReactivate = async (userId, username) => {
    try {
      const result = await window.zignagenixAPI.reactivateUser(userId);
      if (result?.error) { setError(refusalMessage(result)); return; }
      setSuccess(`User "${username}" reactivated successfully`);
      noteRowChanged(userId);
      fetchUsers();
    } catch (err) { setError('Failed to reactivate user'); }
  };

  const handleLock = async (userId, username) => {
    try {
      const result = await window.zignagenixAPI.adminLockAccount(userId);
      if (result?.error) { setError(result.message); return; }
      setSuccess(`Account "${username}" has been locked`);
      fetchUsers();
    } catch (err) { setError('Failed to lock account'); }
  };

  const handleUnlock = async (userId, username) => {
    try {
      const result = await window.zignagenixAPI.adminUnlockAccount(userId);
      if (result?.error) { setError(result.message); return; }
      setSuccess(`Account "${username}" unlocked successfully`);
      fetchUsers();
    } catch (err) { setError('Failed to unlock account'); }
  };

  const handleReset2fa = async (userId, username) => {
    if (!window.confirm(`Reset 2FA for "${username}"?\n\nThis clears their current authenticator. The user's next login will re-enroll 2FA — 2FA is NOT disabled.`)) return;
    try {
      const result = await window.zignagenixAPI.invoke('users:admin-reset-2fa', userId);
      if (result?.error) { setError(result.message); return; }
      setSuccess(`2FA reset for "${username}" — they will re-enroll on next login`);
      fetchUsers();
    } catch (err) { setError('Failed to reset 2FA'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const roleBadge = (role) => {
    const c = ROLE_COLORS[role] || ROLE_COLORS.other;
    return (
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 99,
        fontSize: 11, fontWeight: 600, background: c.bg, color: c.color,
        border: `1px solid ${c.border}`,
      }}>
        {roleLabel(role)}
      </span>
    );
  };

  const statusBadge = (u) => {
    const status = u.status || (u.is_active ? 'active' : 'suspended');
    const c = STATUS_COLORS[status] || STATUS_COLORS.active;
    return (
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 99,
        fontSize: 11, fontWeight: 600,
        background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      }}>
        {c.label}
      </span>
    );
  };

  const getUserStatus = (u) => u.status || (u.is_active ? 'active' : 'suspended');

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ ...NEU.statIcon, background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff' }}>
            <Users size={22} />
          </div>
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>User Management</h1>
            <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>
              {users.length} total users &bull; {users.filter(u => getUserStatus(u) === 'active').length} active
              &bull; {users.filter(u => getUserStatus(u) === 'suspended').length} suspended
              &bull; {users.filter(u => getUserStatus(u) === 'archived').length} archived
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <PrintButton pageTitle="User Management" />
          {canImportStaff && (
            <button className="btn-ghost" onClick={handleImportStaff} disabled={importingStaff}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', fontSize: 13,
                border: '1px solid #E8ECF1', opacity: importingStaff ? 0.6 : 1 }}>
              <Users size={14} /> {importingStaff ? 'Importing...' : 'Import Staff & Team'}
            </button>
          )}
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', fontSize: 14 }}>
            <Plus size={16} /> New User
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{ ...NEU.cardSm, background: '#fef2f2', padding: '12px 16px', marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#991b1b' }}>
          <span>{error}</span>
          <X size={16} style={{ cursor: 'pointer' }} onClick={() => setError('')} />
        </div>
      )}
      {success && (
        <div style={{ ...NEU.cardSm, background: '#f0fdf4', padding: '12px 16px', marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#166534' }}>
          <span>{success}</span>
          <X size={16} style={{ cursor: 'pointer' }} onClick={() => setSuccess('')} />
        </div>
      )}

      {/* Staff Import Results */}
      {staffImportResult && staffImportResult.details && staffImportResult.details.length > 0 && (
        <div style={{ ...NEU.card, padding: 20, marginBottom: 24, borderLeft: '4px solid #2E8B98' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1E293B' }}>
              <Users size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6, color: '#2E8B98' }} />
              Staff Import Results — {staffImportResult.created} created, {staffImportResult.skipped} skipped
            </h3>
            <X size={16} style={{ cursor: 'pointer', color: '#94A3B8' }} onClick={() => setStaffImportResult(null)} />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ ...NEU.th, padding: '8px 10px', textAlign: 'left' }}>Name</th>
                  <th style={{ ...NEU.th, padding: '8px 10px', textAlign: 'left' }}>Username</th>
                  <th style={{ ...NEU.th, padding: '8px 10px', textAlign: 'left' }}>Role</th>
                  <th style={{ ...NEU.th, padding: '8px 10px', textAlign: 'left' }}>Staff Title</th>
                  <th style={{ ...NEU.th, padding: '8px 10px', textAlign: 'left' }}>Temp Password</th>
                  <th style={{ ...NEU.th, padding: '8px 10px', textAlign: 'left' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {staffImportResult.details.map((d, i) => (
                  <tr key={i} style={{ background: d.status === 'created' ? '#f0fdf4' : '#fefce8' }}>
                    <td style={{ ...NEU.td, padding: '8px 10px', fontWeight: 600 }}>{d.name}</td>
                    <td style={{ ...NEU.td, padding: '8px 10px', fontFamily: 'monospace' }}>{d.username || '—'}</td>
                    <td style={{ ...NEU.td, padding: '8px 10px' }}>{d.role || '—'}</td>
                    <td style={{ ...NEU.td, padding: '8px 10px', fontSize: 12, color: '#64748B' }}>{d.roleTitle || '—'}</td>
                    <td style={{ ...NEU.td, padding: '8px 10px', fontFamily: 'monospace', fontSize: 11 }}>
                      {d.tempPassword || '—'}
                    </td>
                    <td style={{ ...NEU.td, padding: '8px 10px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                        background: d.status === 'created' ? '#dcfce7' : '#fef9c3',
                        color: d.status === 'created' ? '#166534' : '#854d0e',
                      }}>
                        {d.status === 'created' ? 'Created' : 'Skipped'}
                      </span>
                      {d.reason && <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 6 }}>({d.reason})</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Admin Quick Actions — Reset Password / Lock Account */}
      {isAdmin && (
        <div style={{ ...NEU.card, padding: 20, marginBottom: 24, borderLeft: '4px solid #DC2626' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Shield size={18} style={{ color: '#DC2626' }} />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1E293B' }}>Admin Quick Actions</h3>
            <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 4 }}>Restricted to administrators</span>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Searchable user dropdown */}
            <div ref={quickRef} style={{ position: 'relative', flex: '1 1 300px', minWidth: 250 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Select User</label>
              <div
                onClick={() => setQuickDropdownOpen(!quickDropdownOpen)}
                style={{
                  ...NEU.input, width: '100%', padding: '10px 12px', fontSize: 14, cursor: 'pointer',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: quickSelectedUser ? '#f0fdf4' : '#fff',
                }}>
                <span style={{ color: quickSelectedUser ? '#1E293B' : '#94A3B8' }}>
                  {quickSelectedUser
                    ? `${quickSelectedUser.display_name} (${quickSelectedUser.username})`
                    : 'Search and select a user...'}
                </span>
                <ChevronDown size={16} style={{ color: '#64748B', flexShrink: 0 }} />
              </div>
              {quickDropdownOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: '#fff', border: '1px solid #E8ECF1', borderRadius: 8,
                  boxShadow: '0 10px 20px rgba(0,0,0,0.12)', maxHeight: 280, overflow: 'hidden',
                  marginTop: 4,
                }}>
                  <div style={{ padding: '8px 10px', borderBottom: '1px solid #E8ECF1', position: 'sticky', top: 0, background: '#fff' }}>
                    <div style={{ position: 'relative' }}>
                      <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                      <input
                        autoFocus
                        type="text"
                        value={quickSearch}
                        onChange={e => setQuickSearch(e.target.value)}
                        placeholder="Type to search by name, username, or email..."
                        onClick={e => e.stopPropagation()}
                        style={{ ...NEU.input, width: '100%', paddingLeft: 32, fontSize: 13, padding: '8px 10px 8px 32px' }}
                      />
                    </div>
                  </div>
                  <div style={{ overflowY: 'auto', maxHeight: 220 }}>
                    {quickFilteredUsers.length === 0 ? (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>No users found</div>
                    ) : quickFilteredUsers.map(u => {
                      const isLocked = u.is_locked || (u.locked_until && new Date(u.locked_until) > new Date());
                      return (
                        <div
                          key={u.id}
                          onClick={() => { setQuickSelectedUser(u); setQuickDropdownOpen(false); setQuickSearch(''); }}
                          style={{
                            padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', borderBottom: '1px solid #F3F4F6',
                            background: quickSelectedUser?.id === u.id ? 'rgba(0,188,212,0.06)' : 'transparent',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F5F6FA'}
                          onMouseLeave={e => e.currentTarget.style.background = quickSelectedUser?.id === u.id ? 'rgba(0,188,212,0.06)' : 'transparent'}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>{u.display_name}</div>
                            <div style={{ fontSize: 11, color: '#64748B' }}>{u.username} &bull; {u.email || 'no email'} &bull; {u.role}</div>
                          </div>
                          {isLocked && <Lock size={14} style={{ color: '#DC2626', flexShrink: 0 }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', paddingTop: 20 }}>
              <button
                className="btn-primary"
                disabled={!quickSelectedUser}
                onClick={() => { if (quickSelectedUser) { setResetPasswordUser(quickSelectedUser); } }}
                style={{ padding: '10px 18px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, opacity: quickSelectedUser ? 1 : 0.5 }}>
                <Key size={14} /> Reset Password
              </button>
              <button
                className="btn-sm"
                disabled={!quickSelectedUser}
                onClick={() => { if (quickSelectedUser) { handleReset2fa(quickSelectedUser.id, quickSelectedUser.username); setQuickSelectedUser(null); } }}
                style={{ padding: '10px 18px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, opacity: quickSelectedUser ? 1 : 0.5 }}>
                <Shield size={14} /> Reset 2FA
              </button>
              <button
                className="btn-sm"
                disabled={!quickSelectedUser}
                onClick={() => {
                  if (!quickSelectedUser) return;
                  const isLocked = quickSelectedUser.is_locked || (quickSelectedUser.locked_until && new Date(quickSelectedUser.locked_until) > new Date());
                  if (isLocked) {
                    handleUnlock(quickSelectedUser.id, quickSelectedUser.username);
                  } else {
                    if (window.confirm(`Immediately lock account "${quickSelectedUser.display_name}" (${quickSelectedUser.username})?\n\nThis will prevent them from logging in until unlocked.`)) {
                      handleLock(quickSelectedUser.id, quickSelectedUser.username);
                    }
                  }
                  setQuickSelectedUser(null);
                }}
                style={{
                  padding: '10px 18px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                  opacity: quickSelectedUser ? 1 : 0.5,
                  background: quickSelectedUser?.is_locked ? '#16A34A' : '#DC2626',
                  color: '#fff', border: 'none',
                }}>
                {quickSelectedUser?.is_locked
                  ? <><Unlock size={14} /> Unlock Account</>
                  : <><Lock size={14} /> Lock Account</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ ...NEU.card, padding: 16, marginBottom: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input type="text" placeholder="Search users by name, username, or email..."
              value={searchText} onChange={e => setSearchText(e.target.value)}
              style={{ ...NEU.input, width: '100%', padding: '10px 12px 10px 36px', fontSize: 14 }} />
          </div>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
            style={{ ...NEU.input, padding: '10px 12px', fontSize: 14, cursor: 'pointer' }}>
            <option value="all">All Roles</option>
            {/* The duplicate hardcoded <option value="admin"> that used to sit
                here is removed: VALID_ROLES already contains admin, so this
                dropdown rendered "Admin" twice. Deliberate, stated change. */}
            {VALID_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ ...NEU.input, padding: '10px 12px', fontSize: 14, cursor: 'pointer' }}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="archived">Archived</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div style={NEU.tableWrap}>
        {initialLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            <Users size={40} style={{ color: '#d1d5db', marginBottom: 12 }} />
            <p style={{ margin: 0 }}>No users found</p>
          </div>
        ) : (
          /* `refreshing` may only change PAINT, never layout height — a height
             change would shrink scrollHeight and reintroduce the clamp this PR
             removes. Opacity qualifies; a spinner that occupies space or a
             placeholder swap does not. */
          <div className="responsive-scroll" aria-busy={refreshing}
            style={{ opacity: refreshing ? 0.65 : 1, transition: 'opacity 0.15s' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr>
                {[
                  // #265: username now lives in the SAME cell as the display name
                  // (below it, smaller and lighter). `Ana Ituarte` vs
                  // `Anna Karen Suenaga` is genuinely hard to tell apart at a
                  // glance; `ana.ituarte` vs `annakaren.suenaga` is not. Merging
                  // them also returns a whole column's width to the table, which
                  // is what was forcing names to wrap to two and three lines.
                  { key: 'display_name', label: 'Name' },
                  { key: 'email', label: 'Email' },
                  { key: 'role', label: 'Role' },
                  { key: 'status', label: 'Status' },
                  { key: 'last_login', label: 'Last Login' },
                ].map(col => (
                  <th key={col.key} onClick={() => handleSort(col.key)}
                    style={{ ...NEU.th, padding: '10px 10px', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {col.label} <SortIcon field={col.key} />
                    </span>
                  </th>
                ))}
                <th style={{ ...NEU.th, padding: '10px 10px', textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u, idx) => {
                const status = getUserStatus(u);

                /* Build dropdown menu items for this user */
                const menuItems = [];
                // RBAC UI-keying: reset-password row action is users:update
                // (admin + sysadmin; board excluded — handler would deny it).
                const isAdmin = !!user?.permissions?.includes('users:update');

                if (u.role !== 'admin' && status !== 'deleted') {
                  menuItems.push({ icon: <Users size={13} />, label: 'Change Role', color: '#7c3aed', onClick: () => setChangeRoleUser(u) });
                  if (status === 'active') {
                    if (isAdmin)
                      menuItems.push({ icon: <Key size={13} />, label: 'Reset Password', onClick: () => setResetPasswordUser(u) });
                    if (canManagePermissions)
                      menuItems.push({ icon: <Shield size={13} />, label: 'Permissions', color: '#2E8B98', onClick: () => onNavigate('permissions') });
                    menuItems.push({ icon: <Pause size={13} />, label: 'Suspend', color: '#d97706', onClick: () => handleSuspend(u.id, u.username) });
                    menuItems.push({ icon: <Archive size={13} />, label: 'Archive', color: '#6b7280', onClick: () => handleArchive(u.id, u.username) });
                  }
                  menuItems.push({ icon: <Trash2 size={13} />, label: 'Delete', color: '#dc2626', onClick: () => handleDelete(u.id, u.username) });
                }
                if (u.role === 'admin') {
                  if (isAdmin)
                    menuItems.push({ icon: <Key size={13} />, label: 'Reset Password', onClick: () => setResetPasswordUser(u) });
                  if (onNavigate)
                    menuItems.push({ icon: <Shield size={13} />, label: 'Permissions', color: '#2E8B98', onClick: () => onNavigate('permissions') });
                }

                return (
                  <tr key={u.id}
                    style={{
                      ...rowSurface(idx, justChangedId === u.id),
                      opacity: status === 'deleted' ? 0.55 : 1,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = HOVER_BG; e.currentTarget.style.boxShadow = NEU.shadow.sm; }}
                    onMouseLeave={e => { const s = rowSurface(idx, justChangedId === u.id); e.currentTarget.style.background = s.background; e.currentTarget.style.boxShadow = s.boxShadow; }}>
                    <td style={{ ...NEU.td, ...CELL, fontWeight: 600, color: '#1f2937' }}>
                      <div style={{ ...TRUNCATE }} title={u.display_name}>{u.display_name}</div>
                      <div style={{ ...TRUNCATE, fontFamily: 'monospace', fontSize: 11.5, color: '#7c8798', fontWeight: 400, marginTop: 1 }}
                        title={u.username}>{u.username}</div>
                    </td>
                    <td style={{ ...NEU.td, ...CELL, color: '#4b5563', fontSize: 13 }}>
                      <div style={TRUNCATE} title={u.email || ''}>{u.email || '—'}</div>
                    </td>
                    <td style={{ ...NEU.td, ...CELL }}>{roleBadge(u.role)}</td>
                    <td style={{ ...NEU.td, ...CELL }}>{statusBadge(u)}</td>
                    <td style={{ ...NEU.td, ...CELL, color: '#6b7280', fontSize: 12 }}>
                      {u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}
                    </td>
                    <td style={{ ...NEU.td, ...CELL, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 3, justifyContent: 'flex-end', alignItems: 'center' }}>
                        {status === 'deleted' ? (
                          <span style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>Deleted</span>
                        ) : (
                          <>
                            {/* Primary inline actions */}
                            {u.role !== 'admin' && status === 'active' && (
                              <button className="btn-ghost btn-sm" title="Edit user"
                                onClick={() => setEditUser(u)}
                                style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 7px', fontSize: 11 }}>
                                <Edit2 size={12} /> Edit
                              </button>
                            )}
                            {u.role === 'admin' && (
                              <span style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic', marginRight: 4 }}>Protected</span>
                            )}
                            {(status === 'suspended' || status === 'archived') && (
                              <button className="btn-ghost btn-sm" title="Reactivate user"
                                onClick={() => setReactivateUser(u)}
                                style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 7px', fontSize: 11, color: '#16a34a' }}>
                                <UserCheck size={12} /> Reactivate
                              </button>
                            )}
                            {/* Lock / Unlock toggle */}
                            {u.is_locked ? (
                              <button className="btn-ghost btn-sm" title="Account is LOCKED — click to unlock"
                                onClick={() => handleUnlock(u.id, u.username)}
                                style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 7px', fontSize: 11, color: '#dc2626', fontWeight: 600, background: '#fef2f2', borderRadius: 4, border: '1px solid #fecaca' }}>
                                <Lock size={12} /> Locked
                              </button>
                            ) : (
                              <button className="btn-ghost btn-sm" title="Account is unlocked — click to lock"
                                onClick={() => handleLock(u.id, u.username)}
                                style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 7px', fontSize: 11, color: '#16a34a', background: '#f0fdf4', borderRadius: 4, border: '1px solid #bbf7d0' }}>
                                <Unlock size={12} /> Unlocked
                              </button>
                            )}
                            {/* More actions dropdown */}
                            {menuItems.length > 0 && <ActionMenu items={menuItems} />}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(msg) => { setSuccess(msg); setShowCreateModal(false); fetchUsers(); }}
          onError={setError}
        />
      )}

      {/* Edit User Modal */}
      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSuccess={(msg, id) => { setSuccess(msg); setEditUser(null); noteRowChanged(id); fetchUsers(); }}
          onError={setError}
        />
      )}

      {/* Change Role Modal */}
      {changeRoleUser && (
        <ChangeRoleModal
          user={changeRoleUser}
          onClose={() => setChangeRoleUser(null)}
          onSuccess={(msg, id) => { setSuccess(msg); setChangeRoleUser(null); noteRowChanged(id); fetchUsers(); }}
          onError={setError}
        />
      )}

      {/* Reactivate Confirmation Modal (#266) */}
      {reactivateUser && (
        <ReactivateModal
          user={reactivateUser}
          onClose={() => setReactivateUser(null)}
          onSuccess={(msg, id) => { setSuccess(msg); setReactivateUser(null); noteRowChanged(id); fetchUsers(); }}
        />
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
          onSuccess={(msg) => { setSuccess(msg); setResetPasswordUser(null); }}
          onError={setError}
        />
      )}
    </div>
  );
}

/* ── Create User Modal ────────────────────────────────────────────────────── */
/* ── In-modal error strip (#263) ──────────────────────────────────────────────
 * A refusal used to be reported ONLY via onError → the page-level red banner at
 * :370. But every one of these modals stays OPEN on error, and NEU.modalOverlay
 * (src/neuStyles.js:218) is `position:fixed; inset:0; zIndex:1000` with a blur
 * scrim — so the banner rendered BEHIND the modal and auto-cleared after 6s. A
 * refusal on these screens was structurally undisplayable: that is why four
 * rejected patient_experience role changes on 2026-08-11 showed the operator
 * nothing at all (#264).
 *
 * This is deliberately the SMALLEST fix that makes a refusal visible — the error
 * is shown where the user is already looking. The wider banner/overlay work
 * belongs to the queued admin-UI card (#234/#263/#265/#266), not here.
 */
function ModalError({ text }) {
  if (!text) return null;
  return (
    <div role="alert" style={{
      margin: '0 0 16px', padding: '10px 12px', borderRadius: 8, fontSize: 13,
      background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
    }}>{text}</div>
  );
}

/** A refusal we could not render is worse than a clumsy one, so never let the
 *  message be empty — see #263: `onError(undefined)` displayed nothing. */
const refusalText = (result, fallback) => refusalMessage(result) || fallback;

function CreateUserModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ username: '', displayName: '', email: '', role: 'nurse', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.displayName.trim() || !form.password.trim()) {
      setErr('Username, display name, and temporary password are required');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const result = await window.zignagenixAPI.createUser(form);
      if (result?.error) { setErr(refusalText(result, 'Failed to create user')); setSaving(false); return; }
      let msg = `User "${form.username}" created (ID: ${result.id}). Password change required on first login.`;
      if (result.emailSent) {
        msg += ` Welcome email sent to ${form.email}.`;
      } else if (form.email) {
        msg += ` Welcome email failed: ${result.emailError || 'unknown error'}.`;
      }
      onSuccess(msg);
    } catch (e) {
      setErr('Failed to create user');
      setSaving(false);
    }
  };

  return (
    <div style={NEU.modalOverlay} onClick={onClose}>
      <div style={{ ...NEU.modal, width: 500, padding: 0 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid rgba(186,199,216,0.3)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
            <Plus size={18} style={{ marginRight: 8, verticalAlign: 'text-bottom', color: NEU.teal }} />
            Create New User
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          <ModalError text={err} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Username *</label>
              <input type="text" value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                placeholder="e.g. jsmith" style={{ ...NEU.input, width: '100%', padding: '10px 12px', fontSize: 14 }} />
            </div>
            <div>
              <label style={labelStyle}>Display Name *</label>
              <input type="text" value={form.displayName}
                onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                placeholder="e.g. Dr. Jane Smith" style={{ ...NEU.input, width: '100%', padding: '10px 12px', fontSize: 14 }} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jane.smith@zignagenix.com" style={{ ...NEU.input, width: '100%', padding: '10px 12px', fontSize: 14 }} />
            </div>
            <div>
              <label style={labelStyle}>Role *</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                style={{ ...NEU.input, width: '100%', padding: '10px 12px', fontSize: 14, cursor: 'pointer' }}>
                {VALID_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Temporary Password *</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Min 12 chars, upper + lower + number + special"
                  style={{ ...NEU.input, width: '100%', padding: '10px 40px 10px 12px', fontSize: 14 }}
                  autoComplete="new-password" />
                <button type="button"
                  onClick={() => setShowPassword(v => !v)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: 4, color: '#6b7280',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 11, color: '#6b7280' }}>
                User will be required to change this on first login. Click the eye icon to toggle visibility while typing.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn-ghost" onClick={onClose}
              style={{ padding: '10px 20px', fontSize: 14 }}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}
              style={{ padding: '10px 20px', fontSize: 14, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Edit User Modal ──────────────────────────────────────────────────────── */
function EditUserModal({ user: target, onClose, onSuccess }) {
  const [form, setForm] = useState({
    displayName: target.display_name || '',
    email: target.email || '',
    role: target.role || 'other',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.displayName.trim()) { setErr('Display name is required'); return; }
    setSaving(true);
    setErr('');
    try {
      const result = await window.zignagenixAPI.updateUser(target.id, form);
      if (result?.error) { setErr(refusalText(result, 'Failed to update user')); setSaving(false); return; }
      onSuccess(`User "${target.username}" updated successfully`, target.id);
    } catch (e2) {
      setErr('Failed to update user');
      setSaving(false);
    }
  };

  return (
    <div style={NEU.modalOverlay} onClick={onClose}>
      <div style={{ ...NEU.modal, width: 500, padding: 0 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid rgba(186,199,216,0.3)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
            <Edit2 size={18} style={{ marginRight: 8, verticalAlign: 'text-bottom', color: NEU.teal }} />
            Edit User: {target.username}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          <ModalError text={err} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Display Name *</label>
              <input type="text" value={form.displayName}
                onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                style={{ ...NEU.input, width: '100%', padding: '10px 12px', fontSize: 14 }} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                style={{ ...NEU.input, width: '100%', padding: '10px 12px', fontSize: 14 }} />
            </div>
            <div>
              <label style={labelStyle}>Role *</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                style={{ ...NEU.input, width: '100%', padding: '10px 12px', fontSize: 14, cursor: 'pointer' }}>
                {VALID_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <p style={{ margin: '6px 0 0', fontSize: 11, color: '#6b7280' }}>
                Changing a role immediately updates sidebar visibility and permission access.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn-ghost" onClick={onClose}
              style={{ padding: '10px 20px', fontSize: 14 }}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}
              style={{ padding: '10px 20px', fontSize: 14, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Change Role Modal ────────────────────────────────────────────────────── */
function ChangeRoleModal({ user: target, onClose, onSuccess }) {
  const [newRole, setNewRole] = useState(target.role || 'other');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const currentLabel = VALID_ROLES.find(r => r.value === target.role)?.label || target.role;
  const newLabel = VALID_ROLES.find(r => r.value === newRole)?.label || newRole;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newRole === target.role) { setErr('Please select a different role'); return; }
    setErr('');
    setConfirming(true);
  };

  const handleConfirm = async () => {
    setSaving(true);
    setErr('');
    try {
      const result = await window.zignagenixAPI.changeUserRole(target.id, newRole);
      if (result?.error) { setErr(refusalText(result, 'Failed to change role')); setSaving(false); setConfirming(false); return; }
      const oldL = VALID_ROLES.find(r => r.value === result.oldRole)?.label || result.oldRole;
      const newL = VALID_ROLES.find(r => r.value === result.newRole)?.label || result.newRole;
      onSuccess(`Role for "${target.username}" changed from ${oldL} to ${newL}`, target.id);
    } catch (e) {
      setErr('Failed to change role');
      setSaving(false);
      setConfirming(false);
    }
  };

  return (
    <div style={NEU.modalOverlay} onClick={onClose}>
      <div style={{ ...NEU.modal, width: 460, padding: 0 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid rgba(186,199,216,0.3)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
            <Users size={18} style={{ marginRight: 8, verticalAlign: 'text-bottom', color: '#7c3aed' }} />
            Change Role
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <X size={20} />
          </button>
        </div>

        {!confirming ? (
          /* Step 1: Select new role */
          <form onSubmit={handleSubmit} style={{ padding: 24 }}>
            {/* #263: a refusal in step 2 calls setConfirming(false), so the user
                lands back HERE — this is where the message has to be. */}
            <ModalError text={err} />
            <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>User</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>{target.display_name} <span style={{ fontWeight: 400, color: '#6b7280' }}>({target.username})</span></div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Current role: <strong>{currentLabel}</strong></div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>New Role *</label>
              <select value={newRole} onChange={e => setNewRole(e.target.value)}
                style={{ ...NEU.input, width: '100%', padding: '10px 12px', fontSize: 14, cursor: 'pointer' }}>
                {VALID_ROLES.map(r => (
                  <option key={r.value} value={r.value}>{r.label}{r.value === target.role ? ' (current)' : ''}</option>
                ))}
              </select>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 11, color: '#dc2626' }}>
              Changing a role immediately updates this user's sidebar visibility, permissions, and data access. This action is audit-logged.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button type="button" className="btn-ghost" onClick={onClose}
                style={{ padding: '10px 20px', fontSize: 14 }}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={newRole === target.role}
                style={{ padding: '10px 20px', fontSize: 14, opacity: newRole === target.role ? 0.6 : 1,
                  background: '#7c3aed' }}>
                Change Role
              </button>
            </div>
          </form>
        ) : (
          /* Step 2: Confirmation */
          <div style={{ padding: 24 }}>
            <ModalError text={err} />
            <div style={{ marginBottom: 20, padding: '16px 20px', background: '#fefce8', borderRadius: 8, border: '1px solid #fef08a' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#854d0e', marginBottom: 10 }}>
                Are you sure you want to update this user's role?
              </div>
              <div style={{ fontSize: 13, color: '#1f2937', lineHeight: 1.6 }}>
                <div><strong>User:</strong> {target.display_name} ({target.username})</div>
                <div><strong>From:</strong> {currentLabel}</div>
                <div><strong>To:</strong> {newLabel}</div>
              </div>
              <div style={{ fontSize: 12, color: '#92400e', marginTop: 10 }}>
                This will immediately change their permissions, sidebar visibility, and data access.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-ghost" onClick={() => setConfirming(false)}
                style={{ padding: '10px 20px', fontSize: 14 }}>Go Back</button>
              <button type="button" className="btn-primary" disabled={saving}
                onClick={handleConfirm}
                style={{ padding: '10px 20px', fontSize: 14, opacity: saving ? 0.6 : 1,
                  background: '#7c3aed' }}>
                {saving ? 'Updating...' : 'Yes, Update Role'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Reactivate Confirmation Modal (#266) ─────────────────────────────────────
 * Deliberately the SAME shape as ChangeRoleModal's confirm step — the pattern
 * that already works — rather than a new one: it echoes the display name, the
 * username (the field that actually disambiguates near-identical names) and
 * what the action will do, and it requires a second, deliberate click.
 */
function ReactivateModal({ user: target, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleConfirm = async () => {
    setSaving(true); setErr('');
    try {
      const result = await window.zignagenixAPI.reactivateUser(target.id);
      if (result?.error) { setErr(refusalText(result, 'Failed to reactivate user')); setSaving(false); return; }
      onSuccess(`User "${target.username}" reactivated successfully`, target.id);
    } catch (e) { setErr('Failed to reactivate user'); setSaving(false); }
  };

  return (
    <div style={NEU.modalOverlay} onClick={onClose}>
      <div style={{ ...NEU.modal, width: 460, padding: 0 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid rgba(186,199,216,0.3)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
            <UserCheck size={18} style={{ marginRight: 8, verticalAlign: 'text-bottom', color: '#16a34a' }} />
            Reactivate Account
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: 24 }}>
          <ModalError text={err} />
          <div style={{ marginBottom: 20, padding: '16px 20px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#166534', marginBottom: 10 }}>
              Are you sure you want to reactivate this account?
            </div>
            <div style={{ fontSize: 13, color: '#1f2937', lineHeight: 1.6 }}>
              <div><strong>User:</strong> {target.display_name}</div>
              <div><strong>Username:</strong> <span style={{ fontFamily: 'monospace' }}>{target.username}</span></div>
              <div><strong>Role:</strong> {roleLabel(target.role)}</div>
            </div>
            <div style={{ fontSize: 12, color: '#4b5563', marginTop: 10 }}>
              They will be able to sign in again immediately, with the permissions their role carries.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-ghost" onClick={onClose}
              style={{ padding: '10px 20px', fontSize: 14 }}>Cancel</button>
            <button type="button" className="btn-primary" disabled={saving} onClick={handleConfirm}
              style={{ padding: '10px 20px', fontSize: 14, opacity: saving ? 0.6 : 1, background: '#16a34a' }}>
              {saving ? 'Reactivating...' : 'Yes, Reactivate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Reset Password Modal ─────────────────────────────────────────────────── */
function ResetPasswordModal({ user: target, onClose, onSuccess, onError }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) { onError('New password is required'); return; }
    setSaving(true);
    try {
      const result = await window.zignagenixAPI.adminResetPassword(target.id, password);
      if (result?.error) { onError(result.message); setSaving(false); return; }
      onSuccess(`Password reset for "${target.username}". They must change it on next login.`);
    } catch (err) {
      onError('Failed to reset password');
      setSaving(false);
    }
  };

  return (
    <div style={NEU.modalOverlay} onClick={onClose}>
      <div style={{ ...NEU.modal, width: 440, padding: 0 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid rgba(186,199,216,0.3)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
            <Key size={18} style={{ marginRight: 8, verticalAlign: 'text-bottom', color: NEU.teal }} />
            Reset Password
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: '#4b5563' }}>
            Set a temporary password for <strong>{target.display_name}</strong> ({target.username}).
            They will be required to change it on next login.
          </p>
          <div>
            <label style={labelStyle}>New Temporary Password *</label>
            <div style={{ position: 'relative' }}>
              <input type={showPassword ? 'text' : 'password'} value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min 12 chars, upper + lower + number + special"
                style={{ ...NEU.input, width: '100%', padding: '10px 40px 10px 12px', fontSize: 14 }}
                autoComplete="new-password" />
              <button type="button"
                onClick={() => setShowPassword(v => !v)}
                title={showPassword ? 'Hide password' : 'Show password'}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: 4, color: '#6b7280',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn-ghost" onClick={onClose}
              style={{ padding: '10px 20px', fontSize: 14 }}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}
              style={{ padding: '10px 20px', fontSize: 14, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151',
};
